import { createAsyncThunk } from "@reduxjs/toolkit"

import * as ErrorsActions from "../../../../Redux/ErrorsRedux"
import {
  downloadFileFromResponse,
  getApiErrorMessage,
  handleApiError
} from "../../../../Utils/thunkHelpers"
import BusinessAccountsActions from "../../../BusinessAccountsPage/reducer"
import * as services from "../services"
import { REQUESTS_RECEIPTS_SLICE_NAME as SLICE_NAME } from "./requestsReceipts.constants"

export const getRequestsReceiptsFilterOptions = createAsyncThunk(
  `${SLICE_NAME}/get_filter_options`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getRequestsReceiptsFilterOptions(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getRequestsReceipts = createAsyncThunk(
  `${SLICE_NAME}/get_requests`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getReceiptsRequests(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getReceiptRequestDetails = createAsyncThunk(
  `${SLICE_NAME}/get_details`,
  async (id, { dispatch, getState, rejectWithValue }) => {
    // Same `test_data` the table and the export send (state.globalFilters.testData): the backend
    // narrows the snapshot lookup by it exactly as it narrows the list, so the detail has to resolve
    // the row in the scope the table is currently listing it under.
    const response = await services.getReceiptRequestDetails(id, {
      test_data: getState().globalFilters.testData
    })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getReceiptProfileUpdateDiff = createAsyncThunk(
  `${SLICE_NAME}/get_profile_update_diff`,
  async (id, { dispatch, getState, rejectWithValue }) => {
    const response = await services.getReceiptProfileUpdateDiff(id, {
      test_data: getState().globalFilters.testData
    })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const updateRequestsReceipt = createAsyncThunk(
  `${SLICE_NAME}/update_receipt`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.updateReceiptRequest(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    // extraAction from legacy saga — dispatch legacy action creator; the legacy
    // saga still handles the `test_data` injection for GET_STATISTICS_ADMIN_REQUEST.
    dispatch(BusinessAccountsActions.getStatisticsAdminRequest())
    return response.data
  }
)

export const updateReceiptStatus = createAsyncThunk(
  `${SLICE_NAME}/update_status`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.updateReceiptStatus(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    dispatch(BusinessAccountsActions.getStatisticsAdminRequest())
    return response.data
  }
)

export const updateReceiptProfileStatus = createAsyncThunk(
  `${SLICE_NAME}/update_profile_status`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.updateReceiptProfileStatus(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    dispatch(BusinessAccountsActions.getStatisticsAdminRequest())
    return response.data
  }
)

export const deleteRequestsReceipt = createAsyncThunk(
  `${SLICE_NAME}/delete_receipt`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteReceiptRequest(id)
    // apiGeneratorDelete accepted either 2xx-with-body or 204 as success.
    const isSuccess = response.ok || response.status === 204
    if (!isSuccess) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    dispatch(BusinessAccountsActions.getStatisticsAdminRequest())
    return {
      id,
      message: response.data?.message?.detail || response.data?.message
    }
  }
)

export const exportReceiptProfileDetails = createAsyncThunk(
  `${SLICE_NAME}/export_profile_details`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.exportReceiptProfileDetails(id)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    downloadFileFromResponse(response, `receipt-${id}.csv`)
  }
)

export const getRequestsReceiptsExport = createAsyncThunk(
  `${SLICE_NAME}/get_export`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getRequestsReceiptsExport(params)
    if (!response.ok) {
      // The over-limit refusal is a bare `{"Export canceled": "..."}` body — no `message` or
      // `detail` for `getApiErrorMessage` to resolve, so it would notify nothing. The raw body goes
      // to the notification instead, which renders each key as its own title and description; that
      // is how the alerts and credit-request exports show the same refusal.
      const message = await getApiErrorMessage(response)
      if (message) await handleApiError(dispatch, response)
      else dispatch(ErrorsActions.errorSave(response.data))
      return rejectWithValue(response.data)
    }
    return response.data
  }
)
